import { useContext } from 'react';
import { styled } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Field, FormikProvider, FormikValues, useFormik } from "formik";
import * as Yup from 'yup';
import Button from "../../../../customs/Button";
import Input from "../../../../customs/FormInput";
import Select from "../../../../customs/SelectInput";
import { NotificationContext } from '../../../../providers/notification';
import apiCall from '../../../../utils/apiCall';
import { reOrderFetchedData } from '../../../../hooks/FetchData';

interface Payload {
    ScoreTypeId: number;
    Name: string;
    RouteId: number;
}

interface ComponentProps {
    scoreTypeId: number;
    scoreType: string;
    routeId: number;
    handleClose: () => void;
}

interface Routes {
    RouteName: string;
    RouteId: number;
};

interface RouteData {
    Data: Routes[];
};

interface RoutesResponse {
  data: RouteData;
};

const UpdateScoreType: React.FC<ComponentProps> = ({ handleClose, scoreType, scoreTypeId, routeId }) => {
    const { showNotification } = useContext(NotificationContext);
    const queryClient = useQueryClient();

    const getAllRoutes = async (): Promise<RoutesResponse> => {
        return (await apiCall().get('/Routes/GetAllRoute'))
    };

    const { data, isLoading, isError, error } = useQuery(['get-all-route'], getAllRoutes, {
        enabled: true,
        keepPreviousData: true,
        retry: 0
    });

    const updateScoreType = (data: Payload) => {
        return apiCall().post(`/SetUp/CreateUpdateScoreType`, data);
    };

    const updateScoreTypeMutation = useMutation(['update-country'], updateScoreType, {retry: 0});

    const updateScoreTypeHandler = async (values: FormikValues) => {
        const payload: Payload = {
            ScoreTypeId: scoreTypeId,
            Name: values?.name,
            RouteId: values?.route,
        };

        try {
            await updateScoreTypeMutation.mutateAsync(payload, {
                onSuccess: () => {
                    showNotification({
                        message: 'Score type successfully updated.',
                        type: 'success',
                    });

                    queryClient.refetchQueries(['get-score-type']);
                    handleClose();
                }
            })
        } catch (error: any){
            showNotification({
                message: error?.response?.data?.title || error?.message,
                type: 'error',
            });
        }
    };

    const validationSchema = Yup.object().shape({
        name: Yup.string().matches(/^[A-Za-z\s!@#$%^&*()/]+$/, 'Only text is allowed').required('required'),
    });

    const formik = useFormik<FormikValues>({
        initialValues: {
            name: scoreType || '',
            route: routeId || '',
        },
        onSubmit: (values) => {
            updateScoreTypeHandler(values);
        },
        validationSchema,
    });

    const routeOption =
    data?.data?.Data && data?.data?.Data?.length > 0 ? (
       reOrderFetchedData(data.data?.Data).map((route : Routes ) => (
                <option value={route?.RouteId} key = {route?.RouteId} >
                    {route?.RouteName}
                </option>
            ))
        ) : (
            <option disabled>
                {isLoading ? 'loading....' : isError ? 'error': ''}
            </option>
    )

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
                <FormWrapper>
                    <Field 
                        name='name'
                        type='text'
                        as={Input}
                        label='Score Type'
                        placeholder='Name'
                    />
                    <Field 
                        name='route'
                        type='text'
                        as={Select}
                        label='Route'
                        options= {routeOption}
                        placeholder='Select'
                    />
                </FormWrapper>
                <ButtonWrapper>
                    <ButtonContainer>
                        <StyledButton onClick={handleClose} text='Cancel'/>
                        <StyledButton 
                            type="submit" 
                            text={updateScoreTypeMutation.isLoading ? 'Submitting' : 'Submit'}
                            disabled={updateScoreTypeMutation.isLoading}
                        />
                    </ButtonContainer>
                </ButtonWrapper>
            </form>
        </FormikProvider>
    )
};

export default UpdateScoreType;

const FormWrapper = styled('div')`
    margin-inline: 3.5rem;
`

const ButtonWrapper = styled('div')`
    display: flex;
    justify-content: flex-end;
    margin-block: 2.5rem;
    padding-inline: 3.5rem;
`

const ButtonContainer = styled('div')`
    display: flex;
    gap: 2rem;
`

const StyledButton = styled(Button)`
    padding-block: 1rem;
    padding-inline: 2rem;
`

